import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, LogOut, Mail, Clock, CheckCircle, XCircle, Trash2, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [filterStatus]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API}/admin/support-requests${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load support requests');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/support-requests/${requestId}`, 
        { status: newStatus, notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchRequests();
      setShowDetailDialog(false);
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Delete this support request?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/support-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request deleted');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const openDetail = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || '');
    setShowDetailDialog(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-blue-100 text-blue-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-stone-100 text-stone-600'
    };
    return styles[status] || styles.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    resolved: requests.filter(r => r.status === 'resolved').length
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
          <Button variant="outline" data-testid="logout-btn" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
              Support Requests
            </h2>
            <p className="text-lg text-stone-600">Manage contact form submissions</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-white rounded-2xl border border-stone-100">
              <p className="text-sm text-stone-500">Total</p>
              <p className="text-3xl font-bold text-primary-900">{stats.total}</p>
            </Card>
            <Card className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
              <p className="text-sm text-amber-600">Pending</p>
              <p className="text-3xl font-bold text-amber-700">{stats.pending}</p>
            </Card>
            <Card className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
              <p className="text-sm text-blue-600">In Progress</p>
              <p className="text-3xl font-bold text-blue-700">{stats.in_progress}</p>
            </Card>
            <Card className="p-4 bg-green-50 rounded-2xl border border-green-100">
              <p className="text-sm text-green-600">Resolved</p>
              <p className="text-3xl font-bold text-green-700">{stats.resolved}</p>
            </Card>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-4 mb-6">
            <Label className="text-stone-600">Filter by status:</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40 rounded-xl" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-stone-600">Loading...</p>
            </div>
          ) : requests.length === 0 ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-primary/50" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">No support requests</h3>
              <p className="text-stone-500">Support requests will appear here</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {requests.map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card 
                    className="p-6 bg-white rounded-2xl border border-stone-100 hover:shadow-md transition-all cursor-pointer"
                    data-testid={`request-card-${request.id}`}
                    onClick={() => openDetail(request)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadge(request.status)}`}>
                            {getStatusIcon(request.status)}
                            {request.status?.replace('_', ' ') || 'pending'}
                          </span>
                          <span className="text-xs text-stone-400">{formatDate(request.created_at)}</span>
                        </div>
                        <h3 className="font-medium text-primary-900 mb-1">{request.name}</h3>
                        <p className="text-sm text-stone-500 mb-2">{request.email}</p>
                        <p className="text-sm text-stone-600 line-clamp-2">{request.message}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-request-${request.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteRequest(request.id);
                        }}
                        className="rounded-full h-8 w-8 p-0 text-destructive hover:text-destructive flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="request-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Support Request</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadge(selectedRequest.status)}`}>
                  {getStatusIcon(selectedRequest.status)}
                  {selectedRequest.status?.replace('_', ' ') || 'pending'}
                </span>
                <span className="text-xs text-stone-400">{formatDate(selectedRequest.created_at)}</span>
              </div>

              <div>
                <p className="text-xs font-bold uppercase text-stone-400 mb-1">From</p>
                <p className="font-medium text-primary-900">{selectedRequest.name}</p>
                <a href={`mailto:${selectedRequest.email}`} className="text-sm text-primary hover:underline">
                  {selectedRequest.email}
                </a>
              </div>

              {selectedRequest.subject && (
                <div>
                  <p className="text-xs font-bold uppercase text-stone-400 mb-1">Subject</p>
                  <p className="text-stone-700">{selectedRequest.subject}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-bold uppercase text-stone-400 mb-1">Message</p>
                <p className="text-stone-700 whitespace-pre-wrap bg-stone-50 p-3 rounded-xl">
                  {selectedRequest.message}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Admin Notes</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes..."
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label>Update Status</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedRequest.id, 'pending')}
                    className={`rounded-full ${selectedRequest.status === 'pending' ? 'bg-amber-100 border-amber-300' : ''}`}
                  >
                    Pending
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedRequest.id, 'in_progress')}
                    className={`rounded-full ${selectedRequest.status === 'in_progress' ? 'bg-blue-100 border-blue-300' : ''}`}
                  >
                    In Progress
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedRequest.id, 'resolved')}
                    className={`rounded-full ${selectedRequest.status === 'resolved' ? 'bg-green-100 border-green-300' : ''}`}
                  >
                    Resolved
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedRequest.id, 'closed')}
                    className={`rounded-full ${selectedRequest.status === 'closed' ? 'bg-stone-200 border-stone-300' : ''}`}
                  >
                    Closed
                  </Button>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  variant="outline"
                  className="w-full rounded-full"
                  onClick={() => window.open(`mailto:${selectedRequest.email}?subject=Re: PlayFirst Support`, '_blank')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Reply via Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
